import React from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { 
  Plus, 
  Loader2, 
  Truck,
} from 'lucide-react';

/**
 * Quick Dispatch Dialog - Add product lines to complete a dispatch
 */
export default function QuickDispatchDialog({
  open,
  onOpenChange,
  dispatchLines,
  products,
  batches,
  cobrandings,
  getBatchesForProduct,
  addDispatchLine,
  updateDispatchLine,
  removeDispatchLine,
  handleQuickDispatch,
  saving
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent 
        className="max-w-4xl max-h-[90vh] overflow-y-auto"
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>Complete Dispatch - Add Products</DialogTitle>
          <DialogDescription>
            Add product lines with all required fields to complete the dispatch.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          {dispatchLines.map((line, index) => (
            <div key={index} className="space-y-3 p-4 bg-muted/30 rounded-md border">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-muted-foreground">Line {index + 1}</span>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => removeDispatchLine(index)}
                  disabled={dispatchLines.length === 1}
                  className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                >
                  ×
                </Button>
              </div>
              
              {/* Row 1: Product, Brand/Pkg */}
              <div className="grid grid-cols-12 gap-3">
                <div className="col-span-6 space-y-1">
                  <Label className="text-xs font-medium">Product *</Label>
                  <Select value={line.product_id} onValueChange={(v) => updateDispatchLine(index, 'product_id', v)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select product..." />
                    </SelectTrigger>
                    <SelectContent>
                      {products.map(p => (
                        <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-6 space-y-1">
                  <Label className="text-xs font-medium">Brand/Pkg</Label>
                  <Select 
                    value={line.cobranding_id || ''} 
                    onValueChange={(v) => updateDispatchLine(index, 'cobranding_id', v === '_none_' ? '' : v)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Standard" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none_">Standard</SelectItem>
                      {cobrandings.map(c => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {/* Row 2: Batch (select or enter), Use By */}
              <div className="grid grid-cols-12 gap-3">
                <div className="col-span-4 space-y-1">
                  <Label className="text-xs font-medium">Batch *</Label>
                  <Select 
                    value={line.batch_id || ''} 
                    onValueChange={(v) => {
                      if (v === '_manual_') {
                        updateDispatchLine(index, 'batch_id', '');
                      } else {
                        const batch = batches.find(b => b.id === v);
                        updateDispatchLine(index, 'batch_id', v);
                        if (batch) {
                          updateDispatchLine(index, 'batch_code', batch.batch_code);
                          updateDispatchLine(index, 'use_by_date', batch.use_by_date || '');
                        }
                      }
                    }}
                    disabled={!line.product_id}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select or enter manually..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_manual_">
                        <span className="text-muted-foreground">Enter manually below...</span>
                      </SelectItem>
                      {getBatchesForProduct(line.product_id).map(b => (
                        <SelectItem key={b.id} value={b.id}>
                          {b.batch_code} ({b.use_by_date || 'No date'})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-4 space-y-1">
                  <Label className="text-xs font-medium">Batch Code *</Label>
                  <Input
                    value={line.batch_code || ''}
                    onChange={(e) => updateDispatchLine(index, 'batch_code', e.target.value)}
                    placeholder="e.g., 260205-01"
                    className="font-mono text-sm"
                  />
                </div>
                <div className="col-span-4 space-y-1">
                  <Label className="text-xs font-medium">Use By *</Label>
                  <Input
                    value={line.use_by_date || ''}
                    onChange={(e) => updateDispatchLine(index, 'use_by_date', e.target.value)}
                    placeholder="DD/MM/YYYY"
                    className="font-mono text-sm"
                  />
                </div>
              </div>
              
              {/* Row 3: Qty, Weight, Cases */}
              <div className="grid grid-cols-12 gap-3">
                <div className="col-span-3 space-y-1">
                  <Label className="text-xs font-medium">Qty *</Label>
                  <Input
                    type="number"
                    value={line.quantity}
                    onChange={(e) => updateDispatchLine(index, 'quantity', e.target.value)}
                    placeholder="0"
                    className="font-mono"
                  />
                </div>
                <div className="col-span-3 space-y-1">
                  <Label className="text-xs font-medium">Weight (kg)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={line.weight_kg}
                    onChange={(e) => updateDispatchLine(index, 'weight_kg', e.target.value)}
                    placeholder="0"
                    className="font-mono"
                  />
                </div>
                <div className="col-span-3 space-y-1">
                  <Label className="text-xs font-medium">Cases</Label>
                  <Input
                    type="number"
                    step="0.5"
                    value={line.cases}
                    onChange={(e) => updateDispatchLine(index, 'cases', e.target.value)}
                    placeholder="0"
                    className="font-mono"
                  />
                </div>
                <div className="col-span-3 space-y-1">
                  <Label className="text-xs font-medium">Notes</Label>
                  <Input
                    value={line.notes || ''}
                    onChange={(e) => updateDispatchLine(index, 'notes', e.target.value)}
                    placeholder="Optional notes..."
                    className="text-sm"
                  />
                </div>
              </div>
            </div>
          ))}
          
          <Button variant="outline" size="sm" onClick={addDispatchLine}>
            <Plus className="w-4 h-4 mr-1" />
            Add Line
          </Button>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleQuickDispatch} disabled={saving}>
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Truck className="w-4 h-4 mr-2" />}
            Complete Dispatch
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
